import {
    FormControl, FormLabel, MenuItem, Tooltip
} from '@mui/material';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { TextField } from '..';

import styles from './AddressFieldset.module.scss';


interface AddressFieldsetProps<TFormValues extends FieldValues> {
    className?: string;
    classes?: {
        legend?: string;
    };
    required?: boolean;
    fieldNames: {
        street: FieldPath<TFormValues>;
        line2?: FieldPath<TFormValues>;
        city: FieldPath<TFormValues>;
        state: FieldPath<TFormValues>;
        zip: FieldPath<TFormValues>;
        county?: FieldPath<TFormValues>;
    };
    disabled?: boolean;
    label?: ReactNode;
}

export function AddressFieldset<TFormValues extends FieldValues>({
    className, required, fieldNames, disabled, label, classes
}: AddressFieldsetProps<TFormValues>) {
    return (
        <FormControl
            component="fieldset"
            className={className}
            required={required}
        >
            <FormLabel
                component="legend"
                className={classes?.legend}
            >
                {label || 'Address'}
            </FormLabel>

            <div
                className={clsx(styles.address, {
                    [styles.noCounty]: !fieldNames.county
                })}
            >
                <TextField<TFormValues>
                    name={fieldNames.street}
                    label="Street"
                    className={styles.street}
                    required={required}
                    hideRequiredIndicator={required}
                    disabled={disabled}
                />

                {fieldNames.line2 && (
                    <TextField<TFormValues>
                        name={fieldNames.line2}
                        label="Street line 2 (optional)"
                        className={styles.line2}
                        disabled={disabled}
                    />
                )}

                <TextField<TFormValues>
                    name={fieldNames.city}
                    label="City"
                    className={styles.city}
                    required={required}
                    hideRequiredIndicator={required}
                    disabled={disabled}
                />

                <TextField<TFormValues>
                    name={fieldNames.state}
                    label="State"
                    className={styles.state}
                    required={required}
                    hideRequiredIndicator={required}
                    disabled={disabled}
                    select
                >
                    {states.map(({ abbreviation, name }) => (
                        <MenuItem
                            value={abbreviation}
                            key={abbreviation}
                        >
                            <Tooltip title={name}>
                                <span>{abbreviation}</span>
                            </Tooltip>
                        </MenuItem>
                    ))}
                </TextField>

                <TextField<TFormValues>
                    name={fieldNames.zip}
                    label="Zip"
                    className={styles.zip}
                    required={required}
                    hideRequiredIndicator={required}
                    disabled={disabled}
                    type="number"
                    rules={{
                        pattern: {
                            value: /^\d{5}(?:[-\s]\d{4})?$/,
                            message: 'Enter a valid zip'
                        }
                    }}
                />

                {fieldNames.county && (
                    <TextField<TFormValues>
                        name={fieldNames.county}
                        label="County"
                        className={styles.county}
                        required={required}
                        hideRequiredIndicator={required}
                        disabled={disabled}
                    />
                )}
            </div>
        </FormControl>
    );
}

export const states = [
    {
        name: 'Alabama',
        abbreviation: 'AL'
    },
    {
        name: 'Alaska',
        abbreviation: 'AK'
    },
    {
        name: 'Arizona',
        abbreviation: 'AZ'
    },
    {
        name: 'Arkansas',
        abbreviation: 'AR'
    },
    {
        name: 'California',
        abbreviation: 'CA'
    },
    {
        name: 'Colorado',
        abbreviation: 'CO'
    },
    {
        name: 'Connecticut',
        abbreviation: 'CT'
    },
    {
        name: 'Delaware',
        abbreviation: 'DE'
    },
    {
        name: 'District Of Columbia',
        abbreviation: 'DC'
    },
    {
        name: 'Florida',
        abbreviation: 'FL'
    },
    {
        name: 'Georgia',
        abbreviation: 'GA'
    },
    {
        name: 'Hawaii',
        abbreviation: 'HI'
    },
    {
        name: 'Idaho',
        abbreviation: 'ID'
    },
    {
        name: 'Illinois',
        abbreviation: 'IL'
    },
    {
        name: 'Indiana',
        abbreviation: 'IN'
    },
    {
        name: 'Iowa',
        abbreviation: 'IA'
    },
    {
        name: 'Kansas',
        abbreviation: 'KS'
    },
    {
        name: 'Kentucky',
        abbreviation: 'KY'
    },
    {
        name: 'Louisiana',
        abbreviation: 'LA'
    },
    {
        name: 'Maine',
        abbreviation: 'ME'
    },
    {
        name: 'Maryland',
        abbreviation: 'MD'
    },
    {
        name: 'Massachusetts',
        abbreviation: 'MA'
    },
    {
        name: 'Michigan',
        abbreviation: 'MI'
    },
    {
        name: 'Minnesota',
        abbreviation: 'MN'
    },
    {
        name: 'Mississippi',
        abbreviation: 'MS'
    },
    {
        name: 'Missouri',
        abbreviation: 'MO'
    },
    {
        name: 'Montana',
        abbreviation: 'MT'
    },
    {
        name: 'Nebraska',
        abbreviation: 'NE'
    },
    {
        name: 'Nevada',
        abbreviation: 'NV'
    },
    {
        name: 'New Hampshire',
        abbreviation: 'NH'
    },
    {
        name: 'New Jersey',
        abbreviation: 'NJ'
    },
    {
        name: 'New Mexico',
        abbreviation: 'NM'
    },
    {
        name: 'New York',
        abbreviation: 'NY'
    },
    {
        name: 'North Carolina',
        abbreviation: 'NC'
    },
    {
        name: 'North Dakota',
        abbreviation: 'ND'
    },
    {
        name: 'Ohio',
        abbreviation: 'OH'
    },
    {
        name: 'Oklahoma',
        abbreviation: 'OK'
    },
    {
        name: 'Oregon',
        abbreviation: 'OR'
    },
    {
        name: 'Pennsylvania',
        abbreviation: 'PA'
    },
    {
        name: 'Rhode Island',
        abbreviation: 'RI'
    },
    {
        name: 'South Carolina',
        abbreviation: 'SC'
    },
    {
        name: 'South Dakota',
        abbreviation: 'SD'
    },
    {
        name: 'Tennessee',
        abbreviation: 'TN'
    },
    {
        name: 'Texas',
        abbreviation: 'TX'
    },
    {
        name: 'Utah',
        abbreviation: 'UT'
    },
    {
        name: 'Vermont',
        abbreviation: 'VT'
    },
    {
        name: 'Virginia',
        abbreviation: 'VA'
    },
    {
        name: 'Washington',
        abbreviation: 'WA'
    },
    {
        name: 'West Virginia',
        abbreviation: 'WV'
    },
    {
        name: 'Wisconsin',
        abbreviation: 'WI'
    },
    {
        name: 'Wyoming',
        abbreviation: 'WY'
    }
];
